import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { api, downloadFile } from "@/lib/api";
import { FileDown } from "lucide-react";
import { PageHeader, Panel, useLoad, Loading, FormDialog, Table, act } from "@/components/common";
import { ApprovalQueue } from "@/components/ApprovalQueue";
import ApprovalDetail from "@/components/ApprovalDetail";
import { useAuth } from "@/context/AuthContext";

function Delegations() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const { data, reload } = useLoad(() => api.get("/delegations").then((r) => r.data));
  const { data: users } = useLoad(() => api.get("/delegations/eligible").then((r) => r.data));
  const opts = (users || []).map((u) => ({ value: u.id, label: `${u.name} (${u.role_label} · ${u.level === "peer" ? "peer" : "lower level"})` }));
  return (
    <Panel title="Delegation of authority" testid="delegations-panel" action={<Button size="sm" data-testid="add-delegation-btn" onClick={() => setOpen(true)}>Delegate</Button>}>
      <p className="mb-3 text-xs text-slate-500">Delegate your approval authority to a peer or deputy for a fixed period. Delegation is logged and shown on every item the delegate decides. You can only delegate to a peer at your level or to someone lower in the approval hierarchy (Team → PM → Project Director / Finance → PMO → Steering Committee → CAB), never upwards.</p>
      <Table rows={data || []} cols={[{ k: "user_name", h: "From" }, { k: "delegate_name", h: "To" }, { k: "start", h: "Start" }, { k: "end", h: "End" },
        { k: "x", h: "", r: (d) => d.user_id === user.id && <Button size="sm" variant="ghost" data-testid={`revoke-delegation-${d.id}`} onClick={() => act(api.delete(`/delegations/${d.id}`), "Revoked").then(reload)}>Revoke</Button> }]} empty="No delegations" />
      <FormDialog open={open} onOpenChange={setOpen} title="Delegate approvals" testid="delegation-form" submitLabel="Delegate"
        fields={[{ name: "delegate_id", label: "Delegate", type: "select", options: opts, required: true },
          { name: "start", label: "Start", type: "date", required: true }, { name: "end", label: "End", type: "date", required: true }]}
        onSubmit={async (v) => { await api.post("/delegations", v); toast.success("Delegation created"); reload(); }} />
    </Panel>
  );
}

export default function Approvals() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const mine = useLoad(() => api.get("/approvals?scope=mine").then((r) => r.data));
  const raised = useLoad(() => api.get("/approvals?scope=raised").then((r) => r.data));
  const all = useLoad(() => api.get("/approvals?scope=all").then((r) => r.data));
  const [deep, setDeep] = useState(params.get("open"));
  useEffect(() => {
    setDeep(params.get("open"));
  }, [params]);
  const refresh = () => { mine.reload(); raised.reload(); all.reload(); };
  if (!mine.data) return <Loading error={mine.error} />;
  return (
    <>
      <PageHeader overline="Approval & routing engine" title="Approvals queue" subtitle="Routes are computed from item type, value and project class — routing can be raised, never lowered."
        actions={["cab", "pmo", "sysadmin"].includes(user.role) && <Button variant="outline" data-testid="approvals-cab-pack-btn" onClick={() => act(downloadFile("/packs/cab", "Aspire_CAB_Decision_Pack.pdf"), "Pack downloaded")}><FileDown className="mr-1 h-4 w-4" />CAB decision pack (PDF)</Button>} />
      <Tabs defaultValue="mine">
        <TabsList>
          <TabsTrigger value="mine" data-testid="tab-approvals-mine">Awaiting me ({mine.data.length})</TabsTrigger>
          <TabsTrigger value="raised" data-testid="tab-approvals-raised">Raised by me</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-approvals-all">All in my projects</TabsTrigger>
          <TabsTrigger value="delegation" data-testid="tab-approvals-delegation">Delegation</TabsTrigger>
        </TabsList>
        <TabsContent value="mine"><Panel><ApprovalQueue items={mine.data} onChange={refresh} testid="approvals-mine-table" /></Panel></TabsContent>
        <TabsContent value="raised"><Panel><ApprovalQueue items={raised.data} onChange={refresh} testid="approvals-raised-table" empty="You have not raised anything" /></Panel></TabsContent>
        <TabsContent value="all"><Panel><ApprovalQueue items={all.data} onChange={refresh} testid="approvals-all-table" empty="No approvals" /></Panel></TabsContent>
        <TabsContent value="delegation"><Delegations /></TabsContent>
      </Tabs>
      <ApprovalDetail id={deep} onClose={() => { setDeep(null); setParams({}); }} onDone={refresh} />
    </>
  );
}
